import { useCallback, useEffect, useState } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  Button,
  SkeletonDisplayText,
  SkeletonBodyText,
} from "@shopify/polaris";
import { useApi } from "../hooks/useApi";
import { createOnboardingApi } from "../api/onboarding";
import { useTranslation } from "../hooks/useTranslation";

interface ContractStatus {
  contract_signed: boolean;
  contract_stale: boolean;
  provider_active: boolean;
  signed_contract_url?: string;
}

/**
 * Shows the real provider-contract signing state (from the SignWell webhook or a
 * manual admin action) and the provider-active flag. Independent of the
 * onboarding wizard: contract signing is not implied by finishing onboarding.
 *
 * Polls `/onboarding/status` so a webhook-driven signature or an admin flipping
 * `provider_active` is reflected without a manual reload.
 */
export function ContractStatusCard() {
  const { Call } = useApi({ baseUrl: "/api" });
  const onboardingApi = createOnboardingApi(Call);
  const { t } = useTranslation();

  const [status, setStatus] = useState<ContractStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (attempt = 0) => {
    try {
      const res = await onboardingApi.getStatus();
      if (res.success && res.data) {
        setStatus({
          contract_signed: !!res.data.contract_signed,
          contract_stale: !!res.data.contract_stale,
          provider_active: !!res.data.provider_active,
          signed_contract_url: res.data.signed_contract_url,
        });
      } else if (res.error === "Authentication required" && attempt < 3) {
        // App Bridge isn't initialized yet (see useAppBridge's deferred
        // createApp). Retry quickly instead of leaving the card blank until the
        // 20s poll — otherwise the card is "missing" on a fresh dashboard mount
        // that loses the init race. Mirrors App.tsx's checkOnboarding retry.
        setTimeout(() => load(attempt + 1), 800);
        return; // Keep the skeleton up; don't mark loaded on this transient path.
      }
    } catch {
      // Non-blocking: leave previous state on transient errors.
    }
    setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const openSigningUrl = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await onboardingApi.getSigningUrl();
      const url = res?.data?.signing_url;
      if (url) {
        window.open(url, "_blank");
      } else {
        setError("Δεν ήταν δυνατή η ανάκτηση του συνδέσμου υπογραφής.");
      }
    } catch {
      setError("Σφάλμα κατά την ανάκτηση του συνδέσμου υπογραφής.");
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetches a short-lived direct download URL for our persisted signed PDF and
  // opens it. The backend mints a Supabase Storage signed URL so the browser
  // downloads straight from Storage (single hop) instead of proxying the whole
  // PDF through mc-server.
  const downloadSigned = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await onboardingApi.getSignedContractUrl();
      const url = res?.data?.download_url;
      if (url) {
        window.open(url, "_blank");
      } else {
        setError("Το υπογεγραμμένο έγγραφο δεν βρέθηκε.");
      }
    } catch {
      setError("Σφάλμα κατά τη λήψη του εγγράφου.");
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render a skeleton while the first status fetch is in flight so the card is
  // visible immediately (matching the rest of the dashboard) instead of popping
  // in after the request resolves.
  if (!loaded && !status) {
    return (
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <SkeletonDisplayText size="small" />
            <SkeletonDisplayText size="small" />
          </InlineStack>
          <SkeletonBodyText lines={2} />
        </BlockStack>
      </Card>
    );
  }

  if (!status) {
    return null;
  }

  const unsignedContractUrl = `${import.meta.env.VITE_BACK_SERVICE_URL}/static/novus_contract.pdf`;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            {t("contractStatus.title")}
          </Text>
          {status.provider_active ? (
            <Badge tone="success">{t("contractStatus.providerActive")}</Badge>
          ) : (
            <Badge tone="attention">{t("contractStatus.providerInactive")}</Badge>
          )}
        </InlineStack>

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}

        {/* Not signed yet */}
        {!status.contract_signed && (
          <Banner tone="warning" title={t("contractStatus.unsigned.title")}>
            <p>{t("contractStatus.unsigned.description")}</p>
            <InlineStack gap="300">
              <Button variant="primary" onClick={openSigningUrl} loading={busy}>
                {t("contractStatus.unsigned.signButton")}
              </Button>
              <Button url={unsignedContractUrl} external>
                {t("contractStatus.unsigned.downloadButton")}
              </Button>
            </InlineStack>
          </Banner>
        )}

        {/* Signed but data changed since — only prompt re-sign while provider is not yet active */}
        {status.contract_signed && status.contract_stale && !status.provider_active && (
          <Banner tone="warning" title={t("contractStatus.stale.title")}>
            <p>{t("contractStatus.stale.description")}</p>
            <InlineStack gap="300">
              <Button variant="primary" onClick={openSigningUrl} loading={busy}>
                {t("contractStatus.stale.resignButton")}
              </Button>
              <Button onClick={downloadSigned} loading={busy}>
                {t("contractStatus.stale.downloadPreviousButton")}
              </Button>
            </InlineStack>
          </Banner>
        )}

        {/* Signed and current — provider not yet activated */}
        {status.contract_signed && !status.contract_stale && !status.provider_active && (
          <Banner tone="info" title={t("contractStatus.pending.title")}>
            <p>{t("contractStatus.pending.description")}</p>
          </Banner>
        )}

        {/* Signed, current, and provider is active — allow downloading the signed copy */}
        {status.contract_signed && !status.contract_stale && status.provider_active && (
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              {t("contractStatus.signed.title")}
            </Text>
            <Button onClick={downloadSigned} loading={busy}>
              {t("contractStatus.signed.downloadButton")}
            </Button>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

export default ContractStatusCard;
